import { createElement, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  ContentWorkflow,
  PublisherReviewScanStatus,
  PublisherReviewStatus,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import { useBarRaiserEvaluationService } from '../../contexts/BarRaiserEvaluationServiceContext';
import { useCanActAsBarRaiser } from './useCanActAsBarRaiser';
import { ToolbarAction } from '../../services/ToolbarActionService';
import { SpeedometerIcon } from '../common/SpeedometerIcon';

/** True when the content is in the publisher review stage. */
function isInPublisherReview(workflow: ContentWorkflow | undefined): boolean {
  return workflow !== undefined && workflow.draftState === WorkflowState.PUBLISHER_REVIEW;
}

/**
 * Builds the bar raiser evaluation toolbar tab for a content page and prefetches
 * its questionnaire. The tab only appears during publisher review: a bar raiser
 * can act on it, and once the content is rejected any other role may open it
 * read-only to see the feedback. The load starts as soon as the content becomes
 * openable so the panel is ready before the tab is clicked.
 *
 * Returns a (possibly empty) action list to spread into the page's toolbar.
 */
export function useBarRaiserEvaluationTab(
  workflow: ContentWorkflow | undefined,
): readonly ToolbarAction[] {
  const canActAsBarRaiser = useCanActAsBarRaiser();
  const barRaiserEvaluationService = useBarRaiserEvaluationService();
  const { contentId } = useParams<{ contentId: string }>();

  // The tab appears during publisher review; only a bar raiser can act on it.
  const showEvaluationTab = isInPublisherReview(workflow);
  const canEvaluate = showEvaluationTab && canActAsBarRaiser;

  // Once rejected, the submitted evaluation is the author's feedback, so any
  // non-bar-raiser role may open the tab read-only to view it.
  const canViewFeedback =
    showEvaluationTab &&
    workflow?.publisherReviewInfo?.reviewStatus === PublisherReviewStatus.REJECTED;

  // Check if the scan has finished, so we can hide the evaluation tab while
  // the scan is running so the user cannot hit publish/reject and get an error
  // from the backend.
  const scanStatus = workflow?.publisherReviewInfo?.scanStatus;
  const isScanFinished =
    scanStatus === PublisherReviewScanStatus.COMPLETE ||
    scanStatus === PublisherReviewScanStatus.FAILED;

  // When scan finishes and the user can act, prefetch the questionnaire so the
  // panel is ready before the tab is clicked. `load` is idempotent, so
  // the panel's own mount-time load becomes a no-op.
  useEffect(() => {
    if (isScanFinished && (canEvaluate || canViewFeedback)) {
      barRaiserEvaluationService.load(contentId);
    }
  }, [isScanFinished, canEvaluate, canViewFeedback, barRaiserEvaluationService, contentId]);

  return useMemo<readonly ToolbarAction[]>(
    () =>
      showEvaluationTab
        ? [
            {
              id: 'bar-raiser-evaluation' as const,
              iconSvg: createElement(SpeedometerIcon),
              tooltip: 'Bar raiser evaluation',
              sortOrder: 0.5,
              disabled: !isScanFinished || !(canEvaluate || canViewFeedback),
              disabledReason: !isScanFinished
                ? 'Quality scan is still in progress'
                : [
                    'You are not allowed to',
                    [!canEvaluate ? 'evaluate' : undefined, !canViewFeedback ? 'view' : undefined]
                      .filter((permText) => permText !== undefined)
                      .join(' or '),
                    'bar raiser feedbacks',
                  ].join(' '),
            },
          ]
        : [],
    [showEvaluationTab, isScanFinished, canEvaluate, canViewFeedback],
  );
}
